<template>
  <div class="marketing-redirect-container">
    <div class="redirect-content">
      <div v-if="!isMarketingModuleEnabled" class="feature-disabled-container">
        <div class="disabled-content">
          <i class="fas fa-toggle-off fa-3x text-muted mb-3"></i>
          <h4>Marketing Module Disabled</h4>
          <p>This feature is currently disabled in this environment.</p>
        </div>
      </div>

      <div v-else-if="loading" class="loading-state">
        <h2>Redirecting...</h2>
        <p>Please wait a moment</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
        </div>
        <h2>Unable to Redirect</h2>
        <p class="error-message">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MarketingRedirect',
  
  data() {
    return {
      loading: true,
      error: null
    }
  },

  computed: {
    // Check if marketing module is enabled via feature flag from database
    isMarketingModuleEnabled() {
      return (this.$store.state.FeatureFlag?.marketingModule?.flag || 'ACTIVE') === 'ACTIVE'
    }
  },

  created() {
    // Run redirect logic immediately in created hook (before mounting)
    // Use nextTick to ensure component is ready but don't wait for DOM
    this.$nextTick(() => {
      this.handleRedirect()
    })
  },

  methods: {
    async handleRedirect() {
      // Skip redirect if marketing module is disabled
      if (!this.isMarketingModuleEnabled) {
        this.loading = false
        return
      }
      
      const referralCode = this.$route.query.referral
      
      if (!referralCode) {
        // No referral code - redirect based on auth status immediately
        const token = localStorage.getItem('token')
        if (token) {
          window.location.href = '/dashboard'
        } else {
          window.location.href = '/login'
        }
        return
      }

      try {
        // Get tenant domain from current hostname (this is the actual tenant subdomain)
        let tenantDomain = window.location.hostname
        
        // Remove protocol if present
        tenantDomain = tenantDomain.replace(/^https?:\/\//, '').replace(/^www\./, '')
        
        console.log('Marketing redirect - Tenant Domain:', tenantDomain)
        
        // For production/staging: use API proxy URL directly (skip frontend proxy)
        // For development: use relative path to go through webpack dev server proxy
        const isDevelopment = process.env.NODE_ENV === 'development'
        const apiUrl = isDevelopment 
          ? '/api/marketing/redirect' 
          : `${process.env.VUE_APP_API_PROXY}/api/marketing/redirect`
        
        console.log('Marketing redirect - API URL:', apiUrl, 'Environment:', process.env.NODE_ENV)
        
        const response = await axios.get(apiUrl, {
          params: { referral: referralCode },
          headers: {
            'X-Tenant-Domain': tenantDomain
          }
        })

        if (response.data && response.data.data && response.data.data.redirect_url) {
          // Redirect to target URL immediately without Vue router
          window.location.href = response.data.data.redirect_url
        } else {
          this.error = 'Invalid response from server'
          this.loading = false
        }
      } catch (err) {
        console.error('Marketing redirect error:', err)
        this.error = err.response?.data?.message || 'Failed to process redirect'
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Feature Disabled Container */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

.marketing-redirect-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.redirect-content {
  max-width: 400px;
  width: 100%;
  text-align: center;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-state h2 {
  color: #1a1a1a;
  font-size: 20px;
  font-weight: 600;
  margin: 24px 0 8px;
}

.loading-state p {
  color: #666666;
  font-size: 14px;
  margin: 0;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.error-state h2 {
  color: #1a1a1a;
  font-size: 20px;
  font-weight: 600;
  margin: 24px 0 12px;
}

.error-state p {
  color: #666666;
  font-size: 14px;
  line-height: 1.5;
}

.error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 16px 0 0;
  color: #991b1b;
  font-size: 13px;
  width: 100%;
}
</style>
